/**
 * Custom hooks for billing and subscription management
 */

import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type CadenceChangeResponse,
  changeCadence as changeCadenceApi,
  changeSeats as changeSeatsApi,
  createCheckoutSession,
  createPortalSession,
  fetchInvoices,
  fetchPlans,
  fetchSubscription,
  resumeInviteCheckout as resumeInviteCheckoutApi,
} from '@/lib/billing/billingClient';
import type {
  BillingPlan,
  BillingSubscription,
  CheckoutSessionOptions,
  FetchInvoicesOptions,
  Invoice,
  SeatChangeResponse,
} from '@/lib/billing/types';

const PAGE_SIZE = 10;

interface UseBillingResult {
  subscription: BillingSubscription | null;
  loading: boolean;
  error: string | null;
  hasSubscription: boolean;
  portalLoading: boolean;
  portalError: string | null;
  checkoutLoading: boolean;
  seatChangeLoading: boolean;
  seatChangeError: string | null;
  retry: () => Promise<void>;
  openPortal: () => Promise<void>;
  /**
   * Open Stripe Checkout. Callers MUST supply ``{tier, cadence, seatCount}``;
   * the backend rejects requests without a price selection.
   */
  openCheckout: (options: CheckoutSessionOptions) => Promise<void>;
  /**
   * Change seat count on the active subscription. Returns the updated
   * mirror values + any immediately-charged invoice.
   */
  changeSeats: (seatCount: number) => Promise<SeatChangeResponse>;
  /**
   * Switch from monthly to yearly billing on the current tier. Yearly →
   * monthly is intentionally NOT exposed — that direction is sales-led.
   */
  changeCadenceToYearly: () => Promise<CadenceChangeResponse>;
  cadenceChangeLoading: boolean;
  cadenceChangeError: string | null;
  /**
   * Rebuild a Stripe Checkout session from the user's redeemed paid invite
   * code and redirect into it. Used by the BillingTab "Resume checkout"
   * banner when a customer cancelled their original invite checkout.
   */
  resumeInviteCheckout: () => Promise<void>;
  resumeInviteLoading: boolean;
  resumeInviteError: string | null;
}

/**
 * Hook for managing subscription data and billing portal access.
 */
export function useBilling(orgId: number): UseBillingResult {
  const [subscription, setSubscription] = useState<BillingSubscription | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [seatChangeLoading, setSeatChangeLoading] = useState(false);
  const [seatChangeError, setSeatChangeError] = useState<string | null>(null);
  const [cadenceChangeLoading, setCadenceChangeLoading] = useState(false);
  const [cadenceChangeError, setCadenceChangeError] = useState<string | null>(
    null
  );
  const [resumeInviteLoading, setResumeInviteLoading] = useState(false);
  const [resumeInviteError, setResumeInviteError] = useState<string | null>(
    null
  );

  // Tracks the most recent orgId we kicked a fetch for. Async responses that
  // belong to an earlier orgId (because the parent swapped orgs mid-flight)
  // resolve last and would otherwise overwrite cleared/newer state. We compare
  // each response's orgId against the latest before applying it.
  const latestRequestedOrgIdRef = useRef<number>(0);

  const loadSubscription = useCallback(async () => {
    // Callers commonly pass `currentOrg?.id ?? 0` so the hook can be called
    // unconditionally before the org is resolved. Skip the fetch when the id
    // is falsy/zero to avoid firing a billing request for a non-existent org.
    latestRequestedOrgIdRef.current = orgId;
    if (!orgId) {
      setSubscription(null);
      setError(null);
      setLoading(false);
      return;
    }
    const requestedOrgId = orgId;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSubscription(orgId);
      // Drop stale responses: if the latest requested orgId no longer matches
      // ours, a newer load (possibly for a different org or the cleared/zero
      // state) has superseded us. Applying our result would overwrite it.
      if (latestRequestedOrgIdRef.current !== requestedOrgId) {
        return;
      }
      setSubscription(data);
    } catch (err) {
      if (latestRequestedOrgIdRef.current !== requestedOrgId) {
        return;
      }
      console.error('Error loading subscription:', err);
      setError("We couldn't load your billing information. Try again.");
      setSubscription(null);
    } finally {
      if (latestRequestedOrgIdRef.current === requestedOrgId) {
        setLoading(false);
      }
    }
  }, [orgId]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const retry = useCallback(async () => {
    await loadSubscription();
  }, [loadSubscription]);

  const openPortal = useCallback(async () => {
    try {
      setPortalLoading(true);
      setPortalError(null);
      const session = await createPortalSession(orgId);
      const popup = window.open(session.url, '_blank');
      if (popup === null) {
        Sentry.captureMessage('Billing portal popup blocked by browser', {
          level: 'warning',
        });
        setPortalError(
          'Pop-up was blocked. Please allow pop-ups for this site and try again.'
        );
      }
    } catch (err) {
      console.error('Error opening portal:', err);
      Sentry.captureException(err);
      setPortalError("We couldn't open the billing portal. Try again.");
    } finally {
      setPortalLoading(false);
    }
  }, [orgId]);

  const openCheckout = useCallback(
    async (options: CheckoutSessionOptions) => {
      try {
        setCheckoutLoading(true);
        const session = await createCheckoutSession(orgId, options);
        // Same-tab navigation matches Stripe's recommended flow for
        // server-side checkout: when the user returns post-payment we
        // want them back on the SPA, not in a stale tab.
        window.location.assign(session.url);
      } catch (err) {
        console.error('Error opening checkout:', err);
        throw err;
      } finally {
        setCheckoutLoading(false);
      }
    },
    [orgId]
  );

  const changeSeats = useCallback(
    async (seatCount: number) => {
      try {
        setSeatChangeLoading(true);
        setSeatChangeError(null);
        const result = await changeSeatsApi(orgId, seatCount);
        // Optimistic merge for instant feedback on the seat count. Doesn't
        // touch pending_seat_count / pending_seat_change_at — the
        // seat-change response doesn't carry those fields. The refetch
        // below settles them (and any other server-side state) by hitting
        // BillingSubscriptionView, which hard-refreshes from Stripe and
        // surfaces the current SubscriptionSchedule state.
        setSubscription(prev =>
          prev
            ? {
                ...prev,
                purchased_seats: result.purchased_seats,
                billing_tier: result.billing_tier,
                billing_cadence:
                  (result.billing_cadence as typeof prev.billing_cadence) ?? '',
              }
            : prev
        );
        // Background refetch so the banner reflects schedule release /
        // creation / modification without waiting for a webhook. Don't
        // toggle `loading` (would flicker the page) — this is a silent
        // reconcile. Errors here are non-fatal; the optimistic state is
        // close enough for the user, and the next page load will resync.
        try {
          const fresh = await fetchSubscription(orgId);
          setSubscription(fresh);
        } catch (refetchErr) {
          console.warn(
            'Post-seat-change refetch failed (UI may show stale pending banner):',
            refetchErr
          );
        }
        return result;
      } catch (err) {
        console.error('Error changing seats:', err);
        setSeatChangeError("We couldn't change your seat count. Try again.");
        throw err;
      } finally {
        setSeatChangeLoading(false);
      }
    },
    [orgId]
  );

  const resumeInviteCheckout = useCallback(async () => {
    try {
      setResumeInviteLoading(true);
      setResumeInviteError(null);
      const session = await resumeInviteCheckoutApi(orgId);
      // Same-tab navigation matches the initial paid-invite redirect flow
      // from `complete_oauth_signup` so the cancel/return path stays
      // identical regardless of which checkout attempt the user is on.
      window.location.assign(session.url);
    } catch (err) {
      console.error('Error resuming invite checkout:', err);
      setResumeInviteError("We couldn't resume your checkout. Try again.");
      throw err;
    } finally {
      setResumeInviteLoading(false);
    }
  }, [orgId]);

  const changeCadenceToYearly = useCallback(async () => {
    try {
      setCadenceChangeLoading(true);
      setCadenceChangeError(null);
      const result = await changeCadenceApi(orgId);
      // Cadence change rewrites the subscription's price + may trigger a
      // proration invoice. Refetch the full subscription so per_seat_cents,
      // billing_interval, current_period_end, and any pending state are all
      // consistent with the freshly-modified Stripe state.
      try {
        const fresh = await fetchSubscription(orgId);
        setSubscription(fresh);
      } catch (refetchErr) {
        console.warn(
          'Post-cadence-change refetch failed (UI may show stale state):',
          refetchErr
        );
      }
      return result;
    } catch (err) {
      console.error('Error changing cadence:', err);
      setCadenceChangeError(
        "We couldn't switch your billing cadence. Try again."
      );
      throw err;
    } finally {
      setCadenceChangeLoading(false);
    }
  }, [orgId]);

  return {
    subscription,
    loading,
    error,
    hasSubscription: subscription?.has_active_subscription ?? false,
    portalLoading,
    portalError,
    checkoutLoading,
    seatChangeLoading,
    seatChangeError,
    cadenceChangeLoading,
    cadenceChangeError,
    retry,
    openPortal,
    openCheckout,
    changeSeats,
    changeCadenceToYearly,
    resumeInviteCheckout,
    resumeInviteLoading,
    resumeInviteError,
  };
}

interface UseInvoicesResult {
  invoices: Invoice[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  loadInvoices: (options?: FetchInvoicesOptions) => Promise<void>;
  goToPage: (page: number) => Promise<void>;
  nextPage: () => Promise<void>;
  previousPage: () => Promise<void>;
}

/**
 * Hook for managing paginated invoice history.
 * Invoices are lazily loaded - call loadInvoices() to fetch.
 */
export function useInvoices(orgId: number): UseInvoicesResult {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  // Track current filter options to preserve them during pagination
  const [currentFilters, setCurrentFilters] = useState<
    Omit<FetchInvoicesOptions, 'page' | 'pageSize'>
  >({});

  const loadInvoices = useCallback(
    async (options: FetchInvoicesOptions = {}) => {
      try {
        setLoading(true);
        setError(null);

        const page = options.page || 1;
        // Extract filter options (everything except page and pageSize)

        const { page: _p, pageSize: _ps, ...filterOptions } = options;
        // Update stored filters when new filter options are provided
        if (Object.keys(filterOptions).length > 0) {
          setCurrentFilters(filterOptions);
        }
        const data = await fetchInvoices(orgId, {
          ...options,
          page,
          pageSize: PAGE_SIZE,
        });

        setInvoices(data.results);
        setTotalCount(data.count);
        setCurrentPage(page);
        setHasNext(data.next !== null);
        setHasPrevious(data.previous !== null);
      } catch (err) {
        console.error('Error loading invoices:', err);
        setError("We couldn't load your invoices. Try again.");
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    },
    [orgId]
  );

  const goToPage = useCallback(
    async (page: number) => {
      // Preserve current filters when paginating
      await loadInvoices({ ...currentFilters, page });
    },
    [loadInvoices, currentFilters]
  );

  const nextPage = useCallback(async () => {
    if (hasNext) {
      await goToPage(currentPage + 1);
    }
  }, [hasNext, currentPage, goToPage]);

  const previousPage = useCallback(async () => {
    if (hasPrevious) {
      await goToPage(currentPage - 1);
    }
  }, [hasPrevious, currentPage, goToPage]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return {
    invoices,
    loading,
    error,
    totalCount,
    totalPages,
    currentPage,
    hasNextPage: hasNext,
    hasPreviousPage: hasPrevious,
    loadInvoices,
    goToPage,
    nextPage,
    previousPage,
  };
}

interface UsePlansResult {
  plans: BillingPlan[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Hook for fetching the tier × cadence catalog.
 *
 * Eagerly loads on mount. The plan picker UI consumes ``plans`` directly;
 * each plan carries both monthly + yearly prices so the cadence toggle
 * doesn't trigger a refetch.
 */
export function usePlans(orgId: number): UsePlansResult {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchPlans(orgId);
      setPlans(response.plans);
    } catch (err) {
      console.error('Error loading plans:', err);
      setError("We couldn't load billing plans. Try again.");
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { plans, loading, error, reload };
}
